import { useCallback, useMemo, useState } from "react";
import { EXECUTION_STATUS } from "../../../AutomationStudio/hooks/useReplayExecution";
import { resolveReplayStepStatus } from "../../../AutomationStudio/utils/replayProgressDisplay";
import { resolveStepIndexForReplayLookup } from "../leftPanel/steps/instructionHierarchyUtils";
import { getActiveFlatSteps } from "../../../../features/automationStudioNew/planUtils";
import RightPanelToolbar from "./RightPanelToolbar";
import InstructionsView from "./instructionsView/InstructionsView";
import StepEditorView from "./stepEditor/StepEditorView";
import ExecutionView from "./executionView/ExecutionView";
import GeneratingView from "./generatingView/GeneratingView";
import AIAssistantOverlay from "./aiAssistant/AIAssistantOverlay";
import AutomationStudioEmptyPanel from "./AutomationStudioEmptyPanel";
import SavedPlanLoadingState from "../leftPanel/loading/SavedPlanLoadingState";
import styles from "../../../../styles/automationStudio/automationStudio.module.scss";
import emptyPanelStyles from "../../../../styles/automationStudio/emptyPanel.module.scss";

/**
 * RightPanelContainer - Presentational orchestrator; all state via props.
 */
export default function RightPanelContainer({
    state,
    selectedStep = null,
    instructions = [],
    executionInstructions = null,
    isGenerating = false,
    isExecuting = false,
    executionResults = null,
    replayExecutionResults = null,
    replayProgressMap = {},
    executionReplayProgressMap = null,
    replayExecuting = false,
    replayCurrentStepIndex = null,
    flatStepsForReplay = [],
    screenshots = [],
    logs = [],
    executionFlow = [],
    overallStatus = null,
    testCompleted = false,
    isLoadedFromTestcase = false,
    hasExecutedInSession = false,
    isReplayFlow = false,
    isSavedExecutionRecord = false,
    automationError = null,
    fileSuggestions = [],
    fileDescriptions = {},
    mcpToolsList = [],
    mcpToolMap = {},
    variablesList = [],
    variablesListDescriptions = {},
    showAIAssistant = false,
    onToggleAIAssistant,
    onInstructionsChange,
    onClearInstructions,
    onStepUpdate,
    onBackToInstructions,
    onBackToResults,
    onPrevStep,
    onNextStep,
    onDeleteStep,
    onRunAgain,
    onEditInstructions,
    onStopExecution,
    onSaveExecutionRecord,
    onCopyInstructions,
    onPasteInstructions,
    stepValidationErrors = {},
    testCaseKey = null,
    selectedTestCaseId = "",
    onTestCaseSelect,
    testCaseOptions = [],
    isLoadingTestCases = false,
    testCaseApiError = null,
    hasMoreTestCases = false,
    onTestCaseInputChange,
    onTestCaseDropdownScroll,
    onTestCaseKeyboardNavigation,
    onExport = () => {},
    canExport = false,
    isNlpAutomated = false,
    isExportBusy = false,
    canImport = false,
    onImportClick = () => {},
    onUploadClick = () => {},
    canUploadFile = false,
    sessionId = null,
    showDynamicScriptChip = false,
    deleteStepWarning = null,
    dynamicResult = null,
    isInitialDataLoading = false,
    isLoadingSavedPlan = false,
    showEmptyRightPanel = false,
    onStartAutomation,
    onTryLoginExample,
}) {
    const planInstructionsForExecution = executionInstructions ?? instructions;
    const hasStepsForSave = instructions.some((inst) => inst?.aiSteps && inst.aiSteps.length > 0);
    const isAllStepsValid = !Object.values(stepValidationErrors || {}).some((err) => {
        if (!err) return false;
        if (err.toolError) return true;
        if (Array.isArray(err.argsError) && err.argsError.length > 0) return true;
        if (err.stepError) return true;
        return false;
    });
    const canSaveExecutionRecord = Boolean(
        !isExecuting && hasStepsForSave && !isSavedExecutionRecord && isAllStepsValid
    );

    const [showAIAssistantInternal, setShowAIAssistantInternal] = useState(false);
    const effectiveShowAIAssistant = showAIAssistant ?? showAIAssistantInternal;
    const toggleAIAssistant = onToggleAIAssistant || (() => setShowAIAssistantInternal((prev) => !prev));
    const hasInstructions = instructions.some((inst) => inst?.text?.trim());

    const { showPreviewTabForStep, selectedFlatStepIndex } = useMemo(() => {
        if (!selectedStep?.id) {
            return { showPreviewTabForStep: false, selectedFlatStepIndex: null };
        }

        if (isLoadedFromTestcase && (!replayExecutionResults?.stepDetails || Object.keys(replayExecutionResults.stepDetails).length === 0) && (!replayProgressMap || Object.keys(replayProgressMap).length === 0)) {
            return { showPreviewTabForStep: false, selectedFlatStepIndex: null };
        }

        const hasReplayProgress = replayProgressMap && Object.keys(replayProgressMap).length > 0;
        const flatStepsForReplay = getActiveFlatSteps(instructions || []);

        for (const inst of instructions || []) {
            if (inst.removedFromEditor) continue;
            const steps = inst.aiSteps || [];
            for (const s of steps) {
                if (s.id === selectedStep.id) {
                    const flatIndex = typeof s.stepIndex === 'number'
                        ? s.stepIndex
                        : flatStepsForReplay.findIndex((step) => step.id === s.id);
                    const stepIndexForLookup = resolveStepIndexForReplayLookup(
                        s,
                        flatIndex,
                        flatStepsForReplay
                    );
                    const replayActive = isReplayFlow || replayExecuting;

                    if (replayActive && hasReplayProgress) {
                        const status = resolveReplayStepStatus(
                            s,
                            stepIndexForLookup,
                            replayProgressMap,
                            true,
                            instructions
                        );
                        const showPreview = [EXECUTION_STATUS.PASSED, EXECUTION_STATUS.FAILED].includes(status);
                        return { showPreviewTabForStep: showPreview, selectedFlatStepIndex: stepIndexForLookup };
                    }

                    const executedStatuses = ['success', 'failed', 'completed'];
                    const hasExecutedStatus = executedStatuses.includes(s.status);
                    const hasScreenshot =
                        s.screenshot ||
                        s.screenshotPath ||
                        s.details?.screenshotPath ||
                        s.executionGroup?.mainAction?.details?.screenshotPath;

                    const showPreview = !!(hasExecutedStatus || hasScreenshot);
                    return { showPreviewTabForStep: showPreview, selectedFlatStepIndex: stepIndexForLookup };
                }
            }
        }

        return { showPreviewTabForStep: false, selectedFlatStepIndex: null };
    }, [selectedStep?.id, instructions, replayProgressMap, replayExecutionResults, isLoadedFromTestcase, isReplayFlow, replayExecuting]);

    const optionsWithLoading = useMemo(() => (
        isLoadingTestCases && hasMoreTestCases && testCaseOptions.length > 0
            ? [
                ...testCaseOptions,
                {
                    isLoadingPlaceholder: true,
                    id: "loading-placeholder",
                    title: "Loading more...",
                },
            ]
            : testCaseOptions
    ), [hasMoreTestCases, isLoadingTestCases, testCaseOptions]);

    const handleTestCaseChange = useCallback((eventOrValue) => {
        let selectedValue = null;
        if (eventOrValue?.target && typeof eventOrValue.target === "object") {
            selectedValue = eventOrValue.target.value ?? null;
        } else if (eventOrValue && typeof eventOrValue === "object" && "value" in eventOrValue) {
            selectedValue = eventOrValue.value;
        } else {
            selectedValue = eventOrValue ?? null;
        }
        onTestCaseSelect?.(selectedValue || null);
    }, [onTestCaseSelect]);

    const renderMainContent = () => {
        if (showEmptyRightPanel) {
            return (
                <AutomationStudioEmptyPanel
                    onStartAutomation={onStartAutomation}
                    onTryExample={onTryLoginExample}
                />
            );
        }

        if (isLoadingSavedPlan) {
            return (
                <div className={emptyPanelStyles.loadingPlaceholder}>
                    <SavedPlanLoadingState />
                </div>
            );
        }

        if (!selectedTestCaseId && isInitialDataLoading) {
            return (
                <div className={emptyPanelStyles.loadingPlaceholder}>
                    <div className={emptyPanelStyles.loadingSpinner} aria-label="Loading" />
                </div>
            );
        }

        return renderView();
    };

    const renderView = () => {
        switch (state) {
            case 'INSTRUCTIONS_VIEW':
                return (
                    <InstructionsView
                        instructions={instructions}
                        onInstructionsChange={onInstructionsChange}
                        onClearInstructions={onClearInstructions}
                        testCompleted={testCompleted}
                        onViewResults={onBackToResults}
                        fileSuggestions={fileSuggestions}
                        fileDescriptions={fileDescriptions}
                        isExecuting={isExecuting}
                        showDynamicScriptChip={showDynamicScriptChip}
                        variablesList={variablesList}
                        variablesListDescriptions={variablesListDescriptions}
                    />
                );

            case 'STEP_EDITOR_VIEW': {
                const stepErrors = selectedStep?.id && stepValidationErrors?.[selectedStep.id]
                    ? stepValidationErrors[selectedStep.id]
                    : {};
                const stepWithErrors = selectedStep ? { ...selectedStep, ...stepErrors } : null;
                return (
                    <StepEditorView
                        key={selectedStep?.id ?? 'no-step'}
                        step={stepWithErrors}
                        instructions={instructions}
                        onStepUpdate={onStepUpdate}
                        onBackToInstructions={onBackToInstructions}
                        onBackToResults={onBackToResults}
                        onPrevStep={onPrevStep}
                        onNextStep={onNextStep}
                        onDeleteStep={onDeleteStep}
                        testCompleted={testCompleted}
                        isExecuting={isExecuting}
                        isGenerating={isGenerating}
                        isReplayExecuting={replayExecuting}
                        mcpToolsList={mcpToolsList}
                        mcpToolMap={mcpToolMap}
                        showPreviewTab={showPreviewTabForStep}
                        replayExecutionResults={replayExecutionResults}
                        replayProgressMap={replayProgressMap}
                        replayCurrentStepIndex={replayCurrentStepIndex}
                        selectedFlatStepIndex={selectedFlatStepIndex}
                        isLoadedFromTestcase={isLoadedFromTestcase}
                        hasExecutedInSession={hasExecutedInSession}
                        isReplayFlow={isReplayFlow}
                        isSavedExecutionRecord={isSavedExecutionRecord}
                        fileSuggestions={fileSuggestions}
                        fileDescriptions={fileDescriptions}
                        variablesList={variablesList}
                        variablesListDescriptions={variablesListDescriptions}
                        deleteStepWarning={deleteStepWarning}
                        dynamicResult={dynamicResult}
                    />
                );
            }

            case 'EXECUTION_VIEW': {
                const hasSteps = planInstructionsForExecution.some(inst => inst.aiSteps && inst.aiSteps.length > 0);
                const hasLogs = logs && logs.length > 0;
                const hasScreenshots = screenshots && screenshots.length > 0;
                const shouldShowGenerating = isGenerating && !hasSteps && !hasLogs && !hasScreenshots && !testCompleted;

                if (shouldShowGenerating) {
                    return (
                        <GeneratingView
                            instructions={instructions}
                            isGenerating={isGenerating}
                        />
                    );
                }

                return (
                    <ExecutionView
                        isExecuting={isExecuting}
                        executionResults={executionResults}
                        replayExecutionResults={replayExecutionResults}
                        replayProgressMap={replayProgressMap}
                        replayExecuting={replayExecuting}
                        replayCurrentStepIndex={replayCurrentStepIndex}
                        flatStepsForReplay={flatStepsForReplay}
                        overallStatus={overallStatus}
                        instructions={planInstructionsForExecution}
                        screenshots={screenshots}
                        logs={logs}
                        executionFlow={executionFlow}
                        testCompleted={testCompleted}
                        isReplayFlow={isReplayFlow}
                        hasExecutedInSession={hasExecutedInSession}
                        isGenerating={isGenerating}
                        onRunAgain={onRunAgain}
                        onEditInstructions={onEditInstructions}
                        onStopExecution={onStopExecution}
                        onSaveExecutionRecord={onSaveExecutionRecord}
                        isSavedExecutionRecord={isSavedExecutionRecord}
                        canSaveExecutionRecord={canSaveExecutionRecord}
                        onStepSelect={onBackToInstructions}
                        automationError={automationError}
                        sessionId={sessionId}
                        showDynamicScriptChip={showDynamicScriptChip}
                    />
                );
            }

            default:
                return (
                    <div className={styles.emptyState}>
                        <p>Unknown state: {state}</p>
                    </div>
                );
        }
    };

    return (
        <div className={styles.rightPanelContainer} data-testid="automation-studio-right-panel">
            <RightPanelToolbar
                state={state}
                selectedStep={selectedStep}
                instructions={instructions}
                isExecuting={isExecuting}
                testCompleted={testCompleted}
                testCaseKey={testCaseKey}
                replayExecuting={replayExecuting}
                replayCurrentStepIndex={replayCurrentStepIndex}
                flatStepsForReplay={flatStepsForReplay}
                onBackToInstructions={onBackToInstructions}
                selectedTestCaseId={selectedTestCaseId}
                onTestCaseChange={handleTestCaseChange}
                optionsWithLoading={optionsWithLoading}
                isLoadingTestCases={isLoadingTestCases}
                testCaseApiError={testCaseApiError}
                testCaseOptions={testCaseOptions}
                onTestCaseInputChange={onTestCaseInputChange}
                onTestCaseDropdownScroll={onTestCaseDropdownScroll}
                onTestCaseKeyboardNavigation={onTestCaseKeyboardNavigation}
                hasInstructions={hasInstructions}
                showEmptyRightPanel={showEmptyRightPanel}
                onCopyInstructions={onCopyInstructions}
                onPasteInstructions={onPasteInstructions}
                canImport={canImport}
                onImportClick={onImportClick}
                canExport={canExport}
                onExport={onExport}
                isNlpAutomated={isNlpAutomated}
                isExportBusy={isExportBusy}
                canUploadFile={canUploadFile}
                onUploadClick={onUploadClick}
            />

            <div className={`${styles.rightPanelContent} ${styles[`state-${state.toLowerCase()}`]}`}>
                {renderMainContent()}
            </div>

            {effectiveShowAIAssistant && (
                <AIAssistantOverlay
                    onClose={toggleAIAssistant}
                    currentState={state}
                    selectedStep={selectedStep}
                    executionResults={executionResults}
                />
            )}
        </div>
    );
}
